<script setup lang="ts">
import ControlValidation from "@/components/control-validation.vue";
import { useControlValidation } from "@/composables/use-control-validation";
import type { CustomerDTO } from "@/models/dtos/customer-dto";
import type { CreateContractRequest } from "@/models/requests/create-contract-request";
import { RouteName, router } from "@/router";
import { getCreateContractSchema } from "@/schemas/create-contract-schema";
import { contractsService } from "@/services/contracts-service";
import { customersService } from "@/services/customers-service";
import { notificationsService } from "@/services/notifications-service";
import { onBeforeMount, ref, toRefs } from "vue";

function getDefaultFormData(): Partial<CreateContractRequest> {
  return {
    name: "",
    eppsusId: undefined,

    customerNumber: "",
  } as Partial<CreateContractRequest>;
}

const props = defineProps<{ customerNumber: string }>();
const { customerNumber } = toRefs(props);

const pageLoading = ref(false);
const loading = ref(false);
const formData = ref(getDefaultFormData());
const customer = ref<CustomerDTO | undefined>();

const { result, validate } = useControlValidation(() =>
  getCreateContractSchema(),
);

onBeforeMount(async () => {
  try {
    pageLoading.value = true;
    customer.value = await customersService.getCustomerByCustomerNumber(
      customerNumber.value!,
    );
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid hämtning av kunden.",
      type: "error",
      error,
    });
  } finally {
    pageLoading.value = false;
  }
});

async function onCreate() {
  loading.value = true;

  try {
    const valid = validate(formData.value);
    if (!valid) return;

    formData.value.customerNumber = customerNumber.value;
    const {
      eppsusId,
      name,
      customerNumber: formCustomerNumber,
    } = formData.value;

    const request: CreateContractRequest = {
      customerNumber: formCustomerNumber!,
      eppsusId: eppsusId!,
      name: name!,
    };
    await contractsService.createCustomer(request);
    notificationsService.open({
      text: `Affär för ${customer.value?.name} skapat.`,
      type: "success",
    });
    formData.value = getDefaultFormData();
    await router.push({
      name: RouteName.Customer,
      params: { customerNumber: customer.value?.customerNumber },
    });
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid skapning av affär.",
      type: "error",
      error,
    });
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <section class="section px-0">
    <p class="title is-4">Skapa affär för {{ customer?.name }}</p>

    <app-loader v-if="pageLoading"></app-loader>

    <div v-else class="columns">
      <div class="column is-6">
        <form @submit.prevent="onCreate">
          <div class="field">
            <label class="label" for="name">Namn</label>
            <control-validation
              :result="result"
              field="name"
              #="{ inputClass }"
            >
              <div class="control">
                <input
                  id="name"
                  v-model="formData.name"
                  class="input"
                  type="text"
                  name="name"
                  :class="inputClass"
                />
              </div>
            </control-validation>
          </div>

          <div class="field">
            <label class="label" for="eppsusId">Affärs ID</label>
            <control-validation
              :result="result"
              field="eppsusId"
              #="{ inputClass }"
            >
              <div class="control">
                <input
                  id="eppsusId"
                  v-model.number="formData.eppsusId"
                  class="input"
                  type="number"
                  name="eppsusId"
                  :class="inputClass"
                />
              </div>
            </control-validation>
          </div>

          <div class="control">
            <button
              class="button is-primary"
              type="submit"
              :class="{ 'is-loading': loading }"
            >
              Skapa
            </button>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>
